// controller-redirect-decision-ticket.ts
//
// "Is this decision ticket the one the CONTROLLER wrote from a `redirect
// --text`?" — one answer shared by G2 (D5: such tickets are not plan content
// and may run while the plan is not ready) and phase 06e R3 (only such a
// ticket may supersede a blocked plan). Moved out of g2-plan-content-
// snapshot.ts (B6) so R3 reuses it instead of writing a second, looser check.
//
// `kind`/`sources` prove nothing on their own: a worker can `create` a
// decision ticket and `amend` its sources. What only the controller writes is
// the ledger in the journal (redirect-text-to-decision-ticket.ts: intent
// `redirect_text_to_decision_ticket` with `params.ticket_id`, result
// `applied`), and the ticket must still have the read-only shape it was
// written with (never amended: revision 1).
import type { MissionState } from "./mission-state-reducer.js";
import type { Ticket } from "./schemas/ticket-schema.js";

const REDIRECT_DECISION_OPERATION_KIND = "redirect_text_to_decision_ticket";
const DIRECTIVE_SOURCE_PREFIX = "directive:";

/** D5: ticket ids the controller's redirect ledger names. */
export function controllerRedirectDecisionIds(state: Pick<MissionState, "intents">): Set<string> {
  const ids = new Set<string>();
  for (const intent of Object.values(state.intents)) {
    if (intent.operation_kind !== REDIRECT_DECISION_OPERATION_KIND) continue;
    const id = intent.params.ticket_id;
    if (typeof id === "string") ids.add(id);
  }
  return ids;
}

/** In the ledger AND still in the read-only shape the controller wrote. */
export function isControllerRedirectDecision(ticket: Ticket, ledger: Set<string>): boolean {
  return (
    ledger.has(ticket.id) &&
    ticket.revision === 1 && // never amended since the controller wrote it (review L1)
    ticket.kind === "decision" &&
    ticket.command_adapter === "decision-v1" &&
    ticket.allowed_paths.length === 0
  );
}

/**
 * 06e R3: the directive id behind a controller-made redirect decision, or
 * undefined. All of: exactly one `directive:<id>` source; the ledger operation
 * `redirect-decision-<id>` has an `applied` result and its intent names THIS
 * ticket; and the shape check above.
 */
export function controllerRedirectDecisionDirectiveId(
  ticket: Ticket,
  state: Pick<MissionState, "intents" | "results">,
): string | undefined {
  const directiveSources = ticket.sources.filter((s) => s.startsWith(DIRECTIVE_SOURCE_PREFIX));
  if (directiveSources.length !== 1) return undefined;
  const directiveId = (directiveSources[0] as string).slice(DIRECTIVE_SOURCE_PREFIX.length);
  const operationId = `redirect-decision-${directiveId}`;
  if (state.results[operationId]?.outcome !== "applied") return undefined;
  const intent = state.intents[operationId];
  if (
    intent?.operation_kind !== REDIRECT_DECISION_OPERATION_KIND ||
    intent.params.ticket_id !== ticket.id ||
    intent.params.directive_id !== directiveId
  ) {
    return undefined;
  }
  return isControllerRedirectDecision(ticket, controllerRedirectDecisionIds(state))
    ? directiveId
    : undefined;
}
